// ─────────────────────────────────────────────
// KLOTHIQ — Capital Contribution Modal
// ─────────────────────────────────────────────

import UserGuide from '@/components/help/UserGuide';
import { Input, Select, Textarea } from '@/components/ui/FormControls';
import DatePicker from '@/components/common/DatePicker';
import { useState, useEffect } from 'react';
import { X, IndianRupee, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { usePartners, useRecordCapital } from '@/hooks/useAccounts';
import { parseRpcError, toISODate } from '@/utils/cashbookCalc';

interface CapitalEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenAddPartner?: () => void;
  defaultPartnerId?: string;
}

export default function CapitalEntryModal({
  isOpen,
  onClose,
  onOpenAddPartner,
  defaultPartnerId,
}: CapitalEntryModalProps) {
  const { data: partners, isLoading: loadingPartners } = usePartners();
  const recordCapital = useRecordCapital();

  const [partnerId, setPartnerId] = useState(defaultPartnerId || '');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(() => toISODate(new Date()));
  const [method, setMethod] = useState<'cash' | 'upi' | 'bank_transfer' | 'cheque'>('bank_transfer');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (defaultPartnerId) setPartnerId(defaultPartnerId);
      setDate(toISODate(new Date()));
    }
  }, [isOpen, defaultPartnerId]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!partnerId) {
      toast.error('Please select a partner');
      return;
    }
    const numAmount = parseFloat(amount);
    if (!numAmount || numAmount <= 0) {
      toast.error('Please enter a valid amount greater than 0');
      return;
    }
    if (!date) {
      toast.error('Date is required');
      return;
    }
    const todayStr = toISODate(new Date());
    if (date > todayStr) {
      toast.error('Date cannot be in the future');
      return;
    }

    setSubmitting(true);
    try {
      await recordCapital.mutateAsync({
        partner_id: partnerId,
        amount: numAmount,
        date,
        payment_method: method,
        notes: notes.trim() || null,
      });
      toast.success('Capital contribution recorded successfully');
      onClose();
      // Reset form
      setAmount('');
      setNotes('');
    } catch (err) {
      const parsed = parseRpcError(err);
      toast.error(parsed.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs" data-field-guide-scope>
      <div className="relative w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-xl animate-in fade-in zoom-in-95">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="absolute right-4 top-4 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-start justify-between gap-3 pr-8">
          <h2 className="text-lg font-semibold text-foreground">Record Capital Contribution</h2>
          <UserGuide compact />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Record partner equity/investment contribution. Tracked separately; does not affect bank balance.
        </p>

        <form onSubmit={handleSubmit} noValidate className="mt-5 space-y-4">
          {/* Partner */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label htmlFor="cap-partner" className="text-xs font-medium text-foreground">
                Partner <span className="text-destructive">*</span>
              </label>
              {onOpenAddPartner && (
                <button
                  type="button"
                  onClick={onOpenAddPartner}
                  className="text-xs text-primary hover:underline"
                >
                  + Add Partner
                </button>
              )}
            </div>
            <Select
              id="cap-partner"
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
              className={inputCls}
              disabled={loadingPartners || submitting}
              helpLabel="Capital partner"
            >
              <option value="">Select partner</option>
              {partners?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.share_percent}%)
                </option>
              ))}
            </Select>
          </div>

          {/* Amount */}
          <div className="space-y-1.5">
            <label htmlFor="cap-amount" className="text-xs font-medium text-foreground">
              Amount (₹) <span className="text-destructive">*</span>
            </label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="cap-amount"
                type="number"
                min="0.01"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={`${inputCls} pl-8`}
                disabled={submitting}
                helpLabel="Amount (₹) *"
              />
            </div>
          </div>

          {/* Date & Method */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label htmlFor="cap-date" className="text-xs font-medium text-foreground">
                Date <span className="text-destructive">*</span>
              </label>
              <DatePicker
                clearable={false}
                aria-label="Date"
                id="cap-date"
                value={date}
                onChange={(nextDate) => setDate(nextDate ?? '')}
                disabled={submitting}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="cap-method" className="text-xs font-medium text-foreground">
                Payment Mode
              </label>
              <Select
                id="cap-method"
                value={method}
                onChange={(e) => setMethod(e.target.value as any)}
                className={inputCls}
                disabled={submitting}
                helpLabel="Payment Mode"
              >
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cheque">Cheque</option>
                <option value="upi">UPI</option>
                <option value="cash">Cash</option>
              </Select>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <label htmlFor="cap-notes" className="text-xs font-medium text-foreground">
              Notes / Reference (Optional)
            </label>
            <Textarea
              id="cap-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={inputCls}
              placeholder="e.g. Initial capital contribution"
              disabled={submitting}
              helpLabel="Notes / Reference (Optional)"
            />
          </div>

          {/* Notice Banner */}
          <div className="rounded-lg border border-border/60 bg-muted/30 p-2.5 text-[11px] text-muted-foreground">
            Capital contributions represent partner equity investments and are tracked separately from bank balance and cash accounts.
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border bg-background px-4 py-2 text-xs font-medium text-foreground hover:bg-muted"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !partnerId || !amount}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              <span>Record Capital</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
